import { useEffect, useState } from 'react';
import { History, TrendingUp, TrendingDown } from 'lucide-react';
import api from '../lib/api';
import { formatFrancAdaptive } from '../lib/utils';

// 2026-11-01 (owner) — every finished quarter's final rank + points.
export const topPercent = (s) => Math.max(1, Math.ceil((s.rank / Math.max(1, s.total)) * 100));

const Trend = ({ now, before }) => {
  if (!before) return null;
  const diff = topPercent(before) - topPercent(now);
  if (diff === 0) return null;
  const up = diff > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-[hsl(var(--positive-text))]' : 'text-destructive'}`}>
      <Icon className="h-3 w-3" /> {Math.abs(diff)}%
    </span>
  );
};

const Row = ({ s, prev }) => (
  <li className="flex items-center justify-between gap-3 py-2.5" data-testid={`season-history-row-${s.league_id}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{s.quarter_label}</p>
      <p className="truncate text-xs text-muted-foreground">{s.team_name}</p>
    </div>
    <div className="text-right tabular-nums">
      <p className="text-sm font-bold whitespace-nowrap">
        #{Number(s.rank).toLocaleString()} <span className="font-normal text-muted-foreground">of {Number(s.total).toLocaleString()}</span>
      </p>
      <p className="flex items-center justify-end gap-2 text-xs text-muted-foreground whitespace-nowrap">
        <span>Top {topPercent(s)}%</span>
        <span>{Math.round(s.points || 0).toLocaleString()} pts</span>
        <span>{formatFrancAdaptive(s.net_worth, { tight: true })}</span>
        <Trend now={s} before={prev} />
      </p>
    </div>
  </li>
);

export default function SeasonHistory({ path, showEmpty = false }) {
  const [seasons, setSeasons] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(path).then((r) => { if (alive) setSeasons(r?.data?.seasons || []); }).catch(() => { if (alive) setSeasons([]); });
    return () => { alive = false; };
  }, [path]);
  if (!seasons || (!seasons.length && !showEmpty)) return null;
  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4" data-testid="season-history">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <History className="h-4 w-4 text-[hsl(var(--accent))]" /> Season history
      </h3>
      {seasons.length ? (
        <ul className="divide-y divide-border/60">
          {seasons.map((s, i) => <Row key={s.league_id} s={s} prev={seasons[i + 1]} />)}
        </ul>
      ) : (
        <p className="py-2 text-xs text-muted-foreground" data-testid="season-history-empty">
          Your final rank for each quarter shows up here once a quarter ends.
        </p>
      )}
    </div>
  );
}
